import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { Link, ResponsiveImage } from "@quintype/components";
import { shape, string, object, number, bool, array } from "prop-types";
import { formatToMonthYear } from "../../../utils/date-formatter";
import { STATIC_KEYWORDS } from "../../../../constants";
import { getSectionUrl } from "../../../../utils/story-utils";
import "./category-card.m.css";

export const CategoryCard = ({
  story,
  position,
  backgroundColor,
  showBottomBorder,
  hideImage,
  config,
}) => {
  const fallbackImage = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "fallback-image"], "")
  );

  const pageType = useSelector((state) => get(state, ["qt", "pageType"], ""));

  if (!story) return null;

  const {
    slug,
    headline: storyHeadline,
    subheadline,
    sections = [],
    tags = [],
    ["hero-image-s3-key"]: heroImageS3Key,
    ["hero-image-metadata"]: heroImageMetadata = {},
    ["published-at"]: publishedAt,
    ["first-published-at"]: firstPublishedAt,
  } = story;

  const headline =
    get(story, ["alternative", "home", "default", "headline"], storyHeadline) ||
    storyHeadline;

  const section = sections[0];
  const reportTag = tags.find((tag) => tag.type === "report-type");
  const [svgUrl, setSvgUrl] = useState("");

  useEffect(() => {
    if (reportTag?.id) {
      fetch(`/api/v1/entities/${reportTag.id}`)
        .then(res => res.json())
        .then(data => {
          const url = data?.entity?.["desktop-svg"]?.[0]?.url;
          if (url) setSvgUrl(url);
        })
        .catch(err => console.error("Error fetching entity:", err));
    }
  }, [reportTag?.id]);
  const formattedDate = formatToMonthYear(publishedAt || firstPublishedAt);
  const isPremiumStory = story?.["story-template"] === "premium-story-template" && pageType !== "home-page";

  const storyUrl = `/${slug}`;
  const storyAriaLabel = `Read story: ${headline}`;
  const hasHeroImage = !!heroImageS3Key;

  const wrapperStyleName = `category-card-wrapper${showBottomBorder ? " category-card-wrapper-with-border" : ""
    }${hideImage ? " category-card-wrapper-no-image" : ""}`;
  const contentStyleName = `category-card-content${backgroundColor ? " category-card-content-with-padding" : ""
    }`;

  return (
    <div styleName="category-card" aria-label={headline}>
      <div styleName={wrapperStyleName}>
        {!hideImage && (
          <Link href={storyUrl} aria-label={storyAriaLabel}>
            <figure styleName="category-card-image">
              {hasHeroImage ? (
                <ResponsiveImage
                  slug={heroImageS3Key}
                  metadata={heroImageMetadata}
                  aspectRatio={[16, 9]}
                  defaultWidth={336}
                  widths={[336]}
                  imgParams={{ auto: ["format", "compress"] }}
                  eager={position < 3 ? "above-fold" : "below-fold"}
                  alt={headline || ""}
                />
              ) : (
                <img
                  src={fallbackImage}
                  alt={headline || ""}
                />
              )}

              {svgUrl && (
                <img
                  styleName="category-card-attribute-icon"
                  src={svgUrl}
                  alt={reportTag?.properties?.["desktop-svg"]?.[0]?.caption}
                />
              )}
            </figure>
          </Link>
        )}

        <div styleName={contentStyleName}>
          {hideImage && svgUrl && (
            <div styleName="category-card-svg-icon">
              <img
                src={svgUrl}
                alt={reportTag?.properties?.["desktop-svg"]?.[0]?.caption || ""}
              />
            </div>
          )}

          {section && (
            <Link
              href={getSectionUrl(section)}
              aria-label={`Go to ${section.name} section`}
              styleName="category-card-category"
            >
              {section.name.toUpperCase()}
            </Link>
          )}

          <Link href={storyUrl} aria-label={storyAriaLabel}>
            <h3 styleName="category-card-headline">{headline}</h3>
          </Link>

          {subheadline && (
            <Link href={storyUrl} aria-label={storyAriaLabel}>
              <p styleName="category-card-description">{subheadline}</p>
            </Link>
          )}

          <div styleName="category-card-footer">
            {formattedDate && <div styleName="category-card-date">{formattedDate}</div>}
            {isPremiumStory && <div styleName="category-card-premium">{STATIC_KEYWORDS.PREMIUM}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

CategoryCard.propTypes = {
  story: shape({
    id: string,
    slug: string,
    headline: string,
    subheadline: string,
    "hero-image-s3-key": string,
    "hero-image-metadata": object,
    "published-at": number,
    "first-published-at": number,
    sections: array,
    tags: array,
    metadata: object,
  }),
  position: number,
  backgroundColor: string,
  showBottomBorder: bool,
  hideImage: bool,
  config: object,
};